import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // One-time cleanup of legacy localStorage token from pre-cookie-auth versions.
  // Prevents stale state confusion after the auth migration.
  useEffect(() => {
    try {
      localStorage.removeItem("mmf_token");
      localStorage.removeItem("mmf_user");
    } catch { /* ignore */ }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Cookie is set by server (httpOnly, secure). We only keep user in memory.
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore — clear client state anyway
    }
    setUser(null);
  }, []);

  const hasPerm = useCallback(
    (perm) => {
      if (!user) return false;
      if (user.role === "admin") return true;
      return (user.permissions || []).includes(perm);
    },
    [user]
  );

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, hasPerm }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
